<template>
  <header class="app-header-inner">
      <div class="left-container">
        <DefaultButton :size="button_size" :iconPosition="'Left'" @click="go_to('home')" :theme="'Primary'"
          :styling="is_active('context') ? 'Filled' : 'Inverted'" class="menuitem">
          <template #default>
            <span class="button-text button-text-first">{{ firstButtonText }}</span>
          </template>
          <template #icon>
            <AddIcon :state="is_active('context') ? 'active' : 'inactive'" class="bicon"/>
          </template>
        </DefaultButton>
        <DefaultButton :size="button_size" :iconPosition="'Left'" @click="go_to('history')" :theme="'Primary'"
          :styling="is_active('history') ? 'Filled' : 'Inverted'" class="menuitem">
          <template #default>
            <span class="button-text">Historiske konsultasjoner</span>
          </template>
          <template #icon>
            <TimeClockIcon :state="is_active('history') ? 'active' : 'inactive'" class="bicon"/>
          </template>
        </DefaultButton>
      </div>
      <LogoLarge class="logo" />
      <div class="right-container">
        <DefaultButton :size="button_size" :iconPosition="'Left'" @click="support_toggle" id="intercom_custom_launcher"
          ref="intercom_ref" :theme="'Primary'" :styling="'Inverted'" class="menuitem">
          <template #icon>
            <QuestionIcon :state="'inactive'" class="bicon"/>
          </template>
          <template #default>
            <span class="button-text">Support</span>
          </template>
        </DefaultButton>
        <DefaultButton v-if="is_authenticated" :icon-position="'Left'" :size="button_size" :theme="'Primary'"
          :styling="is_active('profile.billing') ? 'Filled' : 'Inverted'" @click="go_to('profile.index')"
          class="menuitem">
          <template #icon>
            <GearIcon :state="is_active('profile.billing') ? 'active' : 'inactive'" class="bicon"/>
          </template>
          <template #default>
            <span class="button-text">{{ first_name }}</span>
          </template>
        </DefaultButton>
        <a v-if="is_authenticated" href="/account/logout" class="logout">
          Logg ut
        </a>
      </div>
    </header>
</template>

<script setup lang="ts">
import LogoLarge from '@/components/icons/LogoLarge.vue';
import { useIntercomStore } from '@/stores/intercom';
import { useMeStore } from '@/stores/me';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import DefaultButton from './DefaultButton.vue';
import AddIcon from '@/components/icons/AddIcon.vue';
import TimeClockIcon from '@/components/icons/TimeClockIcon.vue';
import QuestionIcon from '@/components/icons/QuestionIcon.vue';
import GearIcon from '@/components/icons/GearIcon.vue';
import { useRouter } from 'vue-router';

const window_width = ref(window.innerWidth);
const me_store = useMeStore();
const { me, is_authenticated } = storeToRefs(me_store);
const intercom = useIntercomStore();
const intercom_open = ref(false);
const intercom_ref = ref<HTMLElement | null>(null);
const router = useRouter();
const button_size = computed(() => (window_width.value < 768 ? 'xs' : 'md'));

const update_windowwidth = () => {
  window_width.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', update_windowwidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', update_windowwidth);
});

const first_name = computed(() => {
  if (me.value) {
    return me.value.name.split(' ')[0];
  }
  return '';
});

const firstButtonText = computed(() => {
  return window_width.value < 768 ? 'Ny' : 'Ny konsultasjon';
});

watch(intercom_ref, (value) => {
  if (value) {
    intercom.init();
  }
});

const go_to = (name: string) => {
  router.push({ name: name });
};

const is_active = (name: string) => {
  return router.currentRoute.value.name === name;
};

const support_toggle = () => {
  intercom_open.value = !intercom_open.value;
};
</script>

<style lang="scss" scoped>
.app-header-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 24px 40px;
  max-width: 1440px;
  margin-right: auto;
  gap: 10px;

  @media (max-width: 767px) {
    padding: 12px 8px;
    gap: 5px;
  }
  
}

.left-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-self: start;

  @media (max-width: 767px) {
    gap: 8px;
  }
}

.right-container {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-self: end;

  @media (max-width: 767px) {
    gap: 8px;
  }
}

.logo {
  justify-self: center;
  flex-shrink: 0;

  @media (max-width: 767px) {
    max-width: 100px;
    width: auto;
  }
}

.menuitem {
  span {
    font-size: 12px;
    font-style: normal;
    font-weight: 570;
    line-height: 130%;
  }
}

.logout {
  color: var(--Support-colors-Red-100);
  font-family: Saans;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

@media (max-width: 767px) {
  .button-text {
    display: none;
  }

  .button-text-first {
    display: inline-block;
  }

  .menuitem span {
    margin: 0;
  }
}

.bicon {
  width: 16px;
  height: 16px;
}


</style>
